<template>
  <section
    class="three-d-browser"
    :class="{ 'is-mobile-layout': mobileLayout }"
    data-unit="three-d-content-browser"
  >
    <div class="three-d-browser-heading">
      <button
        v-if="selectedModel"
        class="button is-white"
        type="button"
        :aria-label="$t('back')"
        @click="showModels"
      >
        <span class="icon"><i class="fas fa-arrow-left" /></span>
      </button>
      <h3 class="title-five">
        {{ selectedModel ? selectedModel.name : $t('threeD.content') }}
      </h3>
    </div>
    <div
      v-if="selectedSnapshot && viewerStatus !== 'error'"
      class="viewer-shell"
    >
      <three-d-snapshot-viewer
        :key="`${selectedSnapshot.id}-${viewerAttemptId}`"
        ref="snapshotViewer"
        :attempt-id="viewerAttemptId"
        :mobile-layout="mobileLayout"
        :snapshot="selectedSnapshot"
        @asset-ready="handleViewerAssetReady"
        @error="handleViewerError"
        @viewer-shell-ready="handleViewerShellReady"
      />
      <div
        v-if="viewerStatus === 'awaitingAsset'"
        class="asset-unavailable"
        role="status"
      >
        <p>{{ $t('failedToLoad', { content: $tc('snapshot', 1) }) }}</p>
        <button
          class="button is-primary"
          type="button"
          @click="showSnapshots"
        >
          {{ $t('back') }}
        </button>
      </div>
    </div>
    <div
      v-else-if="selectedSnapshot"
      class="three-d-error"
      role="alert"
    >
      <p>{{ $t('failedToLoad', { content: $tc('snapshot', 1) }) }}</p>
      <button
        class="button is-primary"
        type="button"
        @click="retryViewer"
      >
        {{ $t('tryAgain') }}
      </button>
    </div>
    <div
      v-else
      class="three-d-tiles"
    >
      <button
        v-for="item in visibleItems"
        :key="`${item.kind}-${item.id}`"
        class="three-d-tile"
        :class="{ 'is-selected': item.kind === 'snapshot' && item.id === selectedSnapshotId }"
        type="button"
        @click="selectItem(item)"
      >
        <span class="thumbnail">
          <img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            :alt="item.name"
          >
          <span
            v-else
            class="fallback-icon icon"
            aria-hidden="true"
          ><i class="fas fa-cube" /></span>
        </span>
        <span class="tile-name">{{ item.name }}</span>
      </button>
    </div>
    <div
      v-if="viewerStatus === 'loadingViewer'"
      class="library-loading-blocker"
    >
      <div
        class="loading-status"
        role="status"
        aria-live="polite"
      >
        <span
          class="icon is-large"
          aria-hidden="true"
        >
          <i class="fas fa-spinner fa-pulse fa-2x" />
        </span>
        <p class="loading-title">
          {{ $t('threeD.loadingViewerAsset') }}
        </p>
        <p class="loading-help">
          {{ $t('threeD.loadingViewerAssetHelp') }}
        </p>
      </div>
      <button
        class="button is-light loading-cancel"
        type="button"
        @click="showSnapshots"
      >
        {{ $t('back') }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ThreeDSnapshotViewer from './ThreeDSnapshotViewer.vue'
import type {
  ThreeDViewerPartSearchSourcePayload,
  ThreeDViewerSearchCarryIn
} from '@/interfaces/library'

type SnapshotViewerApi = {
  setPartSearchSource: (payload: ThreeDViewerPartSearchSourcePayload) => boolean
  setViewerPartSearchQuery: (query: string) => void
}

const store = useStore()
defineProps<{
  mobileLayout?: boolean
}>()
const models = computed(() => store.getters['content/threeDModels'])
const selectedModel = computed(() => store.getters['content/selectedThreeDModel'])
const selectedSnapshot = computed(() => store.getters['content/selectedThreeDSnapshot'])
const selectedSnapshotId = computed(() => store.state.content.toc.selectedThreeDSnapshotId)
const viewerSearchCarryIn = computed<ThreeDViewerSearchCarryIn | null>(() =>
  store.state.content.toc.threeDViewerSearchCarryIn ?? null
)
const snapshotViewer = ref<SnapshotViewerApi | null>(null)
const viewerStatus = ref<'awaitingAsset' | 'error' | 'idle' | 'loadingViewer' | 'ready'>('idle')
const viewerAttemptId = ref(0)
let appliedCarryInRequestId: number | null = null

type ViewerEvent = {
  attemptId: number
  snapshotId: number
}

const visibleItems = computed(() => {
  if (!selectedModel.value) {
    return models.value.map((model: any) => ({
      ...model,
      kind: 'model',
      name: model.name
    }))
  }
  return selectedModel.value.snapshots.map((snapshot: any) => ({
    ...snapshot,
    kind: 'snapshot',
    name: snapshot.snapshotName || String(snapshot.id)
  }))
})

function selectItem (item: any) {
  if (item.kind === 'model') store.dispatch('content/selectThreeDModel', item.id)
  else store.dispatch('content/selectThreeDSnapshot', item)
}

function showModels () {
  store.dispatch('content/selectThreeDModel', null)
}

function showSnapshots () {
  if (selectedModel.value) {
    store.dispatch('content/selectThreeDModel', selectedModel.value.id)
  }
}

function isCurrentViewerAttempt ({ attemptId, snapshotId }: ViewerEvent) {
  return attemptId === viewerAttemptId.value &&
    snapshotId === selectedSnapshotId.value
}

function handleViewerError (event: ViewerEvent) {
  if (
    !isCurrentViewerAttempt(event) ||
    !['awaitingAsset', 'loadingViewer'].includes(viewerStatus.value)
  ) return

  viewerStatus.value = 'error'
}

function handleViewerAssetReady (event: ViewerEvent) {
  if (!isCurrentViewerAttempt(event) || viewerStatus.value !== 'awaitingAsset') return

  viewerStatus.value = 'ready'
  applyViewerSearchCarryIn()
}

function handleViewerShellReady (event: ViewerEvent) {
  if (!isCurrentViewerAttempt(event) || viewerStatus.value !== 'loadingViewer') return

  // Snapshot asset delivery is frontend-only until the backend contract exists.
  // That future loader must call ThreeDSnapshotViewer.setAssetReady only after
  // the selected asset is usable; until then the UI exits loading as unavailable.
  viewerStatus.value = 'awaitingAsset'
}

function retryViewer () {
  appliedCarryInRequestId = null
  viewerAttemptId.value++
  viewerStatus.value = 'loadingViewer'
}

function applyViewerSearchCarryIn () {
  const carryIn = viewerSearchCarryIn.value
  if (
    viewerStatus.value !== 'ready' ||
    !snapshotViewer.value ||
    !carryIn ||
    carryIn.snapshotId !== selectedSnapshotId.value ||
    carryIn.requestId === appliedCarryInRequestId
  ) return false

  snapshotViewer.value.setViewerPartSearchQuery(carryIn.query)
  appliedCarryInRequestId = carryIn.requestId
  return true
}

function setPartSearchSource (payload: ThreeDViewerPartSearchSourcePayload) {
  return snapshotViewer.value?.setPartSearchSource(payload) ?? false
}

watch(viewerSearchCarryIn, applyViewerSearchCarryIn)

watch(
  selectedSnapshotId,
  (snapshotId) => {
    appliedCarryInRequestId = null
    if (snapshotId === null) {
      viewerStatus.value = 'idle'
      return
    }

    viewerAttemptId.value++
    viewerStatus.value = 'loadingViewer'
  },
  { flush: 'sync', immediate: true }
)

defineExpose({ setPartSearchSource })
</script>

<style scoped>
.three-d-browser {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
}
.three-d-browser-heading {
  align-items: center;
  border-bottom: 3px solid currentColor;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
}
.three-d-browser-heading .title-five {
  margin: 0;
}
.three-d-tiles {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(180px, 220px));
}
.three-d-error {
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  min-height: 360px;
}
.asset-unavailable {
  align-items: center;
  background: rgb(245 246 247 / 90%);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  inset: 0;
  justify-content: center;
  padding: 2rem;
  position: absolute;
  text-align: center;
}
.viewer-shell {
  display: flex;
  min-height: 360px;
  position: relative;
}
.loading-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1rem;
}
.loading-help {
  margin-top: 0.75rem;
  max-width: 42rem;
}
.loading-cancel {
  margin-top: 1.5rem;
}
.loading-status {
  align-items: center;
  display: flex;
  flex-direction: column;
}
.library-loading-blocker {
  align-items: center;
  background: rgb(0 0 0 / 65%);
  color: white;
  display: flex;
  flex-direction: column;
  inset: 0;
  justify-content: center;
  padding: 2rem;
  position: fixed;
  text-align: center;
  z-index: 1000;
}
.three-d-tile {
  align-items: stretch;
  background: var(--body-background-color, white);
  border: 2px solid currentColor;
  border-radius: 0.75rem;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 190px;
  overflow: hidden;
  padding: 1rem;
}
.three-d-tile.is-selected {
  border-color: var(--primary);
  box-shadow: 0 0 0 1px var(--primary);
}
.thumbnail {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 125px;
}
.thumbnail img {
  max-height: 125px;
  max-width: 100%;
  object-fit: contain;
}
.fallback-icon {
  font-size: 4rem;
  height: auto;
  width: auto;
}
.tile-name {
  color: var(--link);
  margin-top: 0.75rem;
  overflow-wrap: anywhere;
}

.three-d-browser.is-mobile-layout {
  min-width: 0;
  padding: 0.75rem;
}

.three-d-browser.is-mobile-layout .three-d-browser-heading {
  margin-bottom: 0.75rem;
}

.three-d-browser.is-mobile-layout .three-d-browser-heading .title-five {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.three-d-browser.is-mobile-layout .three-d-tiles {
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(min(9rem, 100%), 1fr));
}

.three-d-browser.is-mobile-layout .three-d-tile {
  min-height: 10rem;
  min-width: 0;
  padding: 0.75rem;
}

.three-d-browser.is-mobile-layout .thumbnail {
  min-height: 7rem;
}

.three-d-browser.is-mobile-layout .thumbnail img {
  max-height: 7rem;
}

.three-d-browser.is-mobile-layout .tile-name {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: normal;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.three-d-browser.is-mobile-layout .viewer-shell {
  display: block;
  min-height: 0;
}

.three-d-browser.is-mobile-layout .library-loading-blocker {
  padding: 1rem;
}
</style>
